// Page-level art direction and copy for the learn group.
// Facts come from src/data/content.js and design/wireframe-2026-09/CONTENT.md. Nothing here adds new facts.
import { AAROH_IMAGES } from "../../data/images.js";

// One entry per guide, in the order of the field manual.
export const GUIDE_ART = {
  "find-a-real-world-problem": {
    stop: "Find it",
    variant: "a",
    photo: AAROH_IMAGES.studentDiscussion,
    alt: "Students talking through an idea during AAROH at IIIT Delhi",
    caption: "Students talking through an idea at AAROH, IIIT Delhi.",
  },
  "write-a-problem-statement": {
    stop: "Frame it",
    variant: "b",
    photo: AAROH_IMAGES.studentSpeaker,
    alt: "A student speaking into a microphone at AAROH",
    caption: "A student explains her team's problem at AAROH.",
  },
  "beginner-hackathon-guide": {
    stop: "Team up",
    variant: "c",
    photo: AAROH_IMAGES.buildSession,
    alt: "A mentor and students working together on laptops at AAROH",
    caption: "A mentor and a team at work during AAROH.",
  },
  "idea-to-prototype": {
    stop: "Test it",
    variant: "d",
    photo: "/images/grid-photo-08-p7.jpg",
    alt: "Students visiting a makerspace at IIT Delhi during Metamorphosis",
    caption: "A makerspace visit at Metamorphosis, IIT Delhi. The programme was led by Tale of Humankind Foundation and IIT Delhi. GRID was the outreach partner.",
  },
  "pitch-a-student-project": {
    stop: "Show it",
    variant: "e",
    photo: AAROH_IMAGES.studentPresentation,
    alt: "A student presenting from a lectern to the AAROH auditorium",
    caption: "A student presents from the lectern at AAROH.",
  },
};

export const minutes = (readTime) => Number(String(readTime).replace(/\D/g, "")) || 0;

// Extra material for each problem area. Questions, starting points and cautions
// are taken from CONTENT.md and from the stakes and roles in content.js.
export const PROBLEM_EXTRA = {
  education: {
    icon: "GraduationCap",
    label: "Education",
    question: "How do students find assignments when updates arrive in several places?",
    photo: AAROH_IMAGES.room,
    alt: "Student teams working across the AAROH auditorium at IIIT Delhi",
    caption: "Teams at work across the AAROH auditorium, IIIT Delhi.",
    begin: "Talk to students and teachers about one repeated difficulty. Map the existing process before proposing another tool.",
    ideas: [
      "Map how assignment updates reach students who share one family phone.",
      "List every channel updates arrive through, then test one clearer path with five students.",
      "Build a low-bandwidth, classroom-aware prototype and ask a teacher to try it.",
    ],
    avoid: [
      "Proposing another tool before you have mapped the process that exists today.",
      "Building without teachers and learners. A response made without them can add friction.",
      "Treating enrolment as proof that teaching, tools or information are usable.",
    ],
    guides: ["find-a-real-world-problem", "write-a-problem-statement"],
  },
  accessibility: {
    icon: "Wheelchair",
    label: "Accessibility",
    question: "Can everyone complete a common task on your school website?",
    photo: "/images/grid-photo-10-p8.jpg",
    alt: "Aurora overall winner Jerovin Floyd Vincent Joseph holding his laptop prize",
    caption: "Jerovin Floyd Vincent Joseph won Aurora with ChillGui, an accessibility project.",
    begin: "Invite people with relevant access needs to describe barriers. Ask permission before observing tasks and let participants choose how to contribute.",
    ideas: [
      "Pick one common task on your school website and watch where people get stuck.",
      "Demonstrate one simplified task flow that helps an older user finish a common phone action.",
      "Test a change with the people who meet that barrier, not with friends who don't.",
    ],
    avoid: [
      "Observing someone's task without asking first.",
      "Adding accessibility at the end. Involve people from the beginning.",
      "Letting one group of users stand in for every disability or context.",
    ],
    example: { to: "/projects", label: "See ChillGui, an Aurora accessibility project" },
    guides: ["find-a-real-world-problem", "write-a-problem-statement", "beginner-hackathon-guide"],
  },
  climate: {
    icon: "Leaf",
    label: "Environment and local resources",
    question: "Where does usable material become waste in your school?",
    photo: "/images/grid-photo-07-p7.jpg",
    alt: "A large student group outside the Lecture Hall Complex at IIT Delhi during Metamorphosis",
    caption: "Students outside the Lecture Hall Complex, IIT Delhi, during Metamorphosis. GRID was the outreach partner.",
    begin: "Observe one waste or resource process. Record quantities and constraints before proposing a change.",
    ideas: [
      "Trace where usable material becomes waste in your school over one week.",
      "Record quantities and constraints for one resource flow, such as water or paper.",
      "Prototype a monitoring or communication tool, and keep the test small enough to measure.",
    ],
    avoid: [
      "Presenting a prototype as a substitute for policy, infrastructure or expert environmental work.",
      "Claiming your project solves climate change. Say what it measured.",
      "Proposing a change before you have recorded what happens now.",
    ],
    guides: ["find-a-real-world-problem", "write-a-problem-statement"],
  },
  health: {
    icon: "Heartbeat",
    label: "Health information",
    question: "Is a public health notice understandable to the people it is meant for?",
    photo: AAROH_IMAGES.roomWide,
    alt: "The main hall at AAROH, IIIT Delhi, with students at their desks",
    caption: "The main hall at AAROH, IIIT Delhi, on day one.",
    begin: "Study the clarity of public information. Do not diagnose, give treatment advice or collect sensitive health information for a beginner project.",
    ideas: [
      "Test whether a public health notice is understandable to the people it is meant for.",
      "Work on a non-clinical access or information gap, with an informed adult involved.",
      "Improve awareness or supportive information without making clinical claims.",
    ],
    avoid: [
      "Diagnosing anyone or giving treatment advice.",
      "Collecting sensitive health information in a beginner project.",
      "Presenting your project as diagnosis or treatment, in any wording.",
      "Testing before an informed adult has reviewed what you plan to do.",
      "Ignoring privacy and stigma. Trust matters as much as the interface.",
    ],
    safety: true,
    guides: ["find-a-real-world-problem", "write-a-problem-statement"],
  },
  "civic-tech": {
    icon: "Bank",
    label: "Public information",
    question: "Can a resident find a clear answer to a common local service question?",
    photo: AAROH_IMAGES.collaboration,
    alt: "Students, mentors and organisers together on stage at AAROH",
    caption: "Students, mentors and organisers together at AAROH.",
    begin: "Use public sources and check their dates. Test whether people can find the information without collecting identity documents.",
    ideas: [
      "Find the point where residents get lost when answering a common local service question.",
      "Map one public-information journey from the user's point of view.",
      "Test a clearer explanation or service path at a small scale, using verified information.",
    ],
    avoid: [
      "Collecting identity documents or personal details to run a test.",
      "Using public information without checking its source and date.",
      "Assuming that putting a confusing process online makes it fair or usable.",
    ],
    guides: ["find-a-real-world-problem", "write-a-problem-statement"],
  },
};
